import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { MessageCircle, ArrowRight, HelpCircle } from "lucide-react";

const FAQ = () => {
  const generalFaqData = [
    {
      id: "account",
      question: "Do I need to create an account to use SnapRefund?",
      answer: "No, you can try SnapRefund without creating an account! We offer a demo version that lets you generate refund requests to see how our AI works. However, creating a free account gives you access to additional features like saving your requests, tracking conversation history, and accessing our Pro features with advanced platform intelligence."
    },
    {
      id: "generate",
      question: "How do I generate a refund request?",
      answer: "Generating a refund request is simple! Just fill out our form with basic information about your purchase: the merchant name, what you bought, the issue you're experiencing, and the amount. Our AI will analyze this information and generate a professional, legally-sound refund request tailored to your specific situation and the merchant's policies."
    },
    {
      id: "edit",
      question: "How do I edit or personalize the message before sending it?",
      answer: "After our AI generates your refund request, you can easily customize it before sending. You can edit the tone (professional, friendly, firm, etc.), modify any specific details, add additional context, or completely rewrite sections. We also provide regeneration options if you want the AI to create a different version of the message."
    },
    {
      id: "legal",
      question: "Are the generated messages legally valid?",
      answer: "Yes! Our AI is trained on consumer protection laws, refund policies, and best practices for dispute resolution. All generated messages include relevant legal references when appropriate, such as consumer rights, return policies, and dispute resolution procedures. However, we always recommend reviewing the message and consulting with legal professionals for complex cases."
    },
    {
      id: "policies",
      question: "Does SnapRefund detect store policies automatically?",
      answer: "Yes, our Pro feature includes Platform Intelligence that automatically detects which platform or store you're dealing with and provides specific insights about their refund policies, dispute processes, and success strategies. This helps tailor your refund request to the specific merchant's procedures and increases your chances of success."
    },
    {
      id: "no-reply",
      question: "What happens if I don't get a reply from the seller?",
      answer: "If you don't receive a response, SnapRefund can help you with escalation strategies. We can generate follow-up messages, help you understand your rights for chargebacks or dispute resolution, and provide guidance on next steps like contacting your payment processor or relevant consumer protection agencies."
    },
    {
      id: "follow-up",
      question: "Can SnapRefund generate follow-up messages?",
      answer: "Absolutely! SnapRefund excels at generating follow-up messages. You can paste the seller's response into our system, and our AI will analyze their reply and generate an appropriate follow-up message. Whether they've partially agreed, completely denied your request, or offered an alternative solution, we'll help you craft the perfect response."
    },
    {
      id: "wrong-message",
      question: "The AI got my message wrong — what should I do?",
      answer: "No problem! If the generated message doesn't match your needs, you have several options: use the regenerate button to get a completely new version, edit the message directly to fix any issues, or use our clarification feature to provide more specific details about what you want. You can also adjust the tone or provide additional context to get better results."
    }
  ];

  const flightClaimsFaqData = [
    {
      id: "flight-payment",
      question: "Do I need to pay to use SnapRefund for flight claims?",
      answer: "Yes, SnapRefund offers monthly subscriptions or one-time passes to access our flight compensation claims feature. However, unlike other flight claim advocacy companies that take 25-40% of your compensation, we don't take any percentage of successful claims - you keep 100% of what you're owed by the airline."
    },
    {
      id: "flight-requirements",
      question: "What do I need to start a compensation claim through SnapRefund?",
      answer: "To start your flight compensation claim, you'll need basic flight information: your departure and arrival airports, flight date, airline, and flight number if available. You'll also need to describe what happened (delay, cancellation, or overbooking). SnapRefund will guide you through providing any additional details needed."
    },
    {
      id: "flight-eligibility",
      question: "Can I check if my flight is eligible for compensation using SnapRefund?",
      answer: "Absolutely! SnapRefund includes a free comprehensive eligibility checker that analyzes your flight details against EU261, US regulations, and the Montreal Convention. Simply enter your flight information and we'll instantly tell you if you qualify for compensation and how much you might be entitled to receive - no payment required for the eligibility check."
    },
    {
      id: "flight-qualification",
      question: "How does SnapRefund determine if I qualify for compensation?",
      answer: "SnapRefund uses advanced algorithms to check your flight against multiple regulations including EU261 (for EU flights), US DOT rules, and international conventions. We consider factors like flight route, delay duration, cancellation timing, airline responsibility, and extraordinary circumstances to determine your eligibility and potential compensation amount."
    },
    {
      id: "flight-contact",
      question: "Does SnapRefund contact the airline for me?",
      answer: "SnapRefund generates a professional, legally-compliant compensation request that you can send directly to the airline. We provide the complete message with all necessary legal references and your passenger rights, but you maintain full control over the communication with the airline."
    },
    {
      id: "flight-submission",
      question: "Will SnapRefund submit the claim on my behalf?",
      answer: "No, SnapRefund empowers you to submit your own claim by providing all the tools and documentation you need. We generate the professional request letter, provide submission guidance, and help with follow-ups, but you remain in control of your claim throughout the process."
    },
    {
      id: "flight-guarantee",
      question: "Can SnapRefund guarantee compensation?",
      answer: "While SnapRefund can't guarantee compensation (as this depends on the airline's response and specific case circumstances), we significantly improve your chances by ensuring your claim is professionally formatted, legally compliant, and includes all necessary documentation and references to passenger rights regulations."
    }
  ];

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="bg-primary/10 p-3 rounded-full">
              <HelpCircle className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Everything you need to know about using SnapRefund to get your money back
          </p>
        </div>

        {/* General FAQ Section */}
        <Card className="mb-8 bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl font-semibold text-center">General Questions</CardTitle>
            <CardDescription className="text-center">Common questions about using SnapRefund</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Accordion type="single" collapsible className="w-full">
              {generalFaqData.map((faq) => (
                <AccordionItem key={faq.id} value={faq.id} className="border-b last:border-b-0">
                  <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-muted/50 transition-colors">
                    <span className="font-medium text-base">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 pt-0">
                    <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* Flight Claims FAQ Section */}
        <Card className="mb-8 bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl font-semibold text-center">Flight Compensation Claims</CardTitle>
            <CardDescription className="text-center">Everything about flight delay and cancellation compensation</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Accordion type="single" collapsible className="w-full">
              {flightClaimsFaqData.map((faq) => (
                <AccordionItem key={faq.id} value={faq.id} className="border-b last:border-b-0">
                  <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-muted/50 transition-colors">
                    <span className="font-medium text-base">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 pt-0">
                    <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Card className="bg-white/80 backdrop-blur-sm border-primary/20">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2 mb-2">
              <MessageCircle className="h-5 w-5" />
              Still have questions?
            </CardTitle>
            <CardDescription className="text-base">
              Can't find what you're looking for? Try our refund request generator or contact our support team.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="gap-2">
              <Link to="/refund">
                Create Refund Request
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link to="/contact">Contact Support</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FAQ;